import React, { useEffect, useState } from 'react';
import {
  Button, FormControl, FormLabel, Input, Modal, Switch, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Select, Text, useToast,
  HStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 733F/813 (Chad) — convert a PO into a customer-facing Sales Order (default) or Work
// Order: copies the hardware lines to a new doc priced at GROUP (their price), off the PO
// line's price (item 810). Optional hardware discount off group. Client PO # + uploaded PO
// carry through. Shared by the PO grid AND the PO form footer so they never diverge.
const PurchaseOrderConvertModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  purchaseOrderId: string;
}> = ({ isOpen, onClose, purchaseOrderId }) => {
  const toast = useToast();
  const history = useHistory();
  const [isSalesOrder, setIsSalesOrder] = useState(true);
  const [discount, setDiscount] = useState('');
  // item 1310 (Chad) — "[has tracking] → URL" on the guided conversion; stamped on the PO and
  // carried to the new SO/WO, then on to the invoice (PDFs + the invoice email hyperlink)
  const [hasTracking, setHasTracking] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => { if (isOpen) { setIsSalesOrder(true); setDiscount(''); setHasTracking(false); setTrackingUrl(''); } }, [isOpen]);

  const run = async () => {
    setConverting(true);
    try {
      const { data: newWoId } = await axios.post(`${apiUrl()}purchaseOrder/${purchaseOrderId}/convert`, {
        isSalesOrder,
        hardwareDiscountPercent: Number(discount) || 0,
        trackingUrl: hasTracking && trackingUrl.trim() ? trackingUrl.trim() : undefined, // item 1310
      });
      onClose();
      toast({ status: 'success', title: `Converted to ${isSalesOrder ? 'Sales Order' : 'Work Order'}`, description: 'Opening it — priced at group.' });
      if (newWoId) history.push(`/WorkOrder/${newWoId}`);
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not convert', description: String(e?.response?.data?.errors?.[0] ?? e?.response?.data ?? 'Try again.') });
    } finally {
      setConverting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Convert purchase order</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel fontSize="sm">Convert to</FormLabel>
            <Select value={isSalesOrder ? 'so' : 'wo'} onChange={(e) => setIsSalesOrder(e.target.value === 'so')}>
              <option value="so">Sales Order (hardware / materials)</option>
              <option value="wo">Work Order (allows labor)</option>
            </Select>
          </FormControl>
          <Text fontSize="sm" mb={4} opacity={0.8}>
            Copies the hardware lines to a new
            {' '}
            {isSalesOrder ? 'Sales Order' : 'Work Order'}
            {' '}
            billed at your group price (their price). The client PO # + uploaded PO carry through.
          </Text>
          <FormControl>
            <FormLabel fontSize="sm">Hardware discount off group (%) — optional</FormLabel>
            <Select value={discount} onChange={(e) => setDiscount(e.target.value)}>
              <option value="">No discount</option>
              {[1, 2, 3, 4, 5, 7.5, 10, 12.5, 15, 20].map((p) => (
                <option key={p} value={p}>{`${p}% off group`}</option>
              ))}
            </Select>
            <Text fontSize="xs" mt={2} opacity={0.6}>Services are never discounted. You can still adjust individual items afterward.</Text>
          </FormControl>
          {/* item 1310 — shipment tracking, captured here once and carried PO → SO → invoice */}
          <FormControl mt={4}>
            <HStack spacing={3}>
              <Switch id="convert-has-tracking" isChecked={hasTracking} onChange={(e) => { setHasTracking(e.target.checked); if (!e.target.checked) setTrackingUrl(''); }} />
              <FormLabel htmlFor="convert-has-tracking" mb={0} fontSize="sm" cursor="pointer">Has tracking</FormLabel>
            </HStack>
          </FormControl>
          {hasTracking && (
            <FormControl mt={2}>
              <Input type="url" placeholder="Carrier tracking URL" value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} />
              <Text fontSize="xs" mt={2} opacity={0.6}>Stamped on this PO and carried to the Sales Order and the invoice — prints on all three PDFs and shows as “Track your shipment” in the invoice email.</Text>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="brandGreen" isLoading={converting} onClick={run}>Convert &amp; open</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PurchaseOrderConvertModal;
